import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { AssessmentSearchCriteria } from '../../../assessment/service/assessment-search-criteria';
import { AssessmentService } from '../../../assessment/service/assessment.service';
import { SpeciesSelectorComponent } from '../species-selector/species-selector.component';
import {
ActionEvent,
ActionState,
MessageDialogComponent,
} from '../../../common/message-dialog/message-dialog.component';
import { ReportService } from '../../service/report.service';
import { PagedResponse } from '../../../domain/response/PagedResponse';
import { AssessmentEntity } from '../../../domain/nztcs/assessment-entity';
import { AllSelectedMode } from './all-selected-enum';

@Component({
selector: 'app-assessment-selector',
templateUrl: './assessment-selector.component.html',
})
export class AssessmentSelectorComponent implements OnChanges {
@ViewChild('selectAll') selectAll;
@ViewChild('speciesSelectorComponent', { static: true })
speciesSelectorComponent: SpeciesSelectorComponent;

@ViewChild('messageDialog', { static: true })
messageDialog: MessageDialogComponent;

public allSelectedModeEnum = AllSelectedMode;
allSelectedMode = AllSelectedMode.none;

selectedIds: Set<number> = new Set();
assessmentSearchCriteria = new AssessmentSearchCriteria();
allAssessmentsSearchCriteria = new AssessmentSearchCriteria();

allAssessmentSearchResponseIds: number[] = [];

filteredAssessmentSearchResponseIds: number[] = null;

response: PagedResponse<AssessmentEntity> = null;

assessmentSearchResponse: any = {
searchResults: [],
numberOfPages: 0,
};

private _reportName;
private _reportId;
private _modifiable;

constructor(
    private assessmentService: AssessmentService,
    private reportService: ReportService
  ) {
    this.assessmentSearchCriteria.getLatestReport = this.getLatestReport;
    this.assessmentSearchCriteria.showArchived = true; // always show archived assessments on the report page (so duplicates can be managed)
  }

  get modifiable() {
    return this._modifiable;
  }

  @Input()
  set modifiable(value) {
    this._modifiable = value;
  }

  @Input()
  public set reportName(reportName: string) {
    this._reportName = reportName;
  }

  @Input()
  public set reportId(reportId: string) {
    this._reportId = reportId;
  }

  @Input()
  public getLatestReport: boolean = false;

  @Input()
  public assessments: PagedResponse<AssessmentEntity> = null;

  ngOnChanges() {
    this.loadData();
    if (this._reportId) {
      this.loadAllAssessments();
    }
  }

  loadingPromises = new Set();

  public get loading() {
    return this.loadingPromises.size > 0;
  }

  loadData() {
    if (this.assessments) {
      // Create a response based on the given assessments;
      const response = new PagedResponse<AssessmentEntity>();
      response.page = this.assessmentSearchCriteria.pageNumber || 1;
      response.pageSize = this.assessmentSearchCriteria.pageSize;
      response.total = this.assessments.total;
      if (this.assessmentSearchCriteria.assessmentName) {
        response.searchResults = this.assessments.searchResults.filter((assessment) =>
          assessment.assessmentName
            .toLowerCase()
            .includes(this.assessmentSearchCriteria.assessmentName.toLowerCase())
        );
        response.total = response.searchResults.length;
      } else {
        response.searchResults = this.assessments.searchResults.slice(response.pageSize * (response.page - 1), response.pageSize * response.page);
      }
      this.response = response;
    } else if (this._reportId) {
      this.assessmentSearchCriteria.reportId = this._reportId;

      this.loadAssessments();
    }
  }

  loadAssessments() {
    let promise;
    promise = this.assessmentService
      .findByCriteria(this.assessmentSearchCriteria)
      .subscribe((pagedResponse: PagedResponse<AssessmentEntity>) => {
        this.response = pagedResponse;
        this.loadingPromises.delete(promise);
      });
    this.loadingPromises.add(promise);
  }

  loadAllAssessments(filter?: string) {
    let promise;
    const criteria = new AssessmentSearchCriteria();
    criteria.reportId = this._reportId;
    criteria.showArchived = true;
    criteria.pageSize = undefined;
    criteria.pageNumber = undefined;

    if (filter) {
      criteria.assessmentName = filter;
    }

    promise = this.assessmentService.findByCriteria(criteria).toPromise().then((r) => {
      if (filter) {
        this.filteredAssessmentSearchResponseIds = r.searchResults.map((a) => a.assessmentId);
      } else {
        this.allAssessmentSearchResponseIds = r.searchResults.map((a) => a.assessmentId);
        if (this.allSelectedMode === AllSelectedMode.all) {
          this.selectedIds = new Set(this.allAssessmentSearchResponseIds);
        }
      }
      this.loadingPromises.delete(promise);
    });
    this.loadingPromises.add(promise);
  }

  onAddSpecies() {
    this.speciesSelectorComponent.start(this._reportId);
  }

  onConfirmRemoveAssessments() {
    const messageText =
      'You are about to remove ' +
      this.selectedIds.size +
      ' assessment records. Are you sure you want to delete these Assessment (not Species) records. This action cannot be undone.';
    this.messageDialog.start(messageText);
  }

  onDeleteAssessmentsActionEvent(actionEvent: ActionEvent) {
    if (actionEvent.state === ActionState.CONFIRMED) {
      this.reportService
        .removeAssessments(this._reportId, [...this.selectedIds.values()])
        .subscribe(() => {
          this.loadAllAssessments();
          this.onReset();
        });
    }
  }

  onSpeciesNameChanged() {
    if (!this.assessments) {
      if (this.assessmentSearchCriteria.assessmentName) {
        this.loadAllAssessments(this.assessmentSearchCriteria.assessmentName);
      } else {
        this.filteredAssessmentSearchResponseIds = null;
      }
    }
    this.assessmentSearchCriteria.pageNumber = 1;
    this.loadData();
    this.selectedIds.clear();
    if (this.selectAll?.nativeElement) {
      this.selectAll.nativeElement.indeterminate = false;
    }
    this.allSelectedMode = AllSelectedMode.none;
  }

  onReset() {
    this.assessmentSearchCriteria.assessmentName = null;
    this.onSpeciesNameChanged();
  }

  onPageChanged(event: any) {
    this.assessmentSearchCriteria.pageNumber = event.page;
    this.loadData();
  }

  toggleSelection(e, i) {
    const assessment = this.response.searchResults[i];

    if (this.allSelectedMode !== AllSelectedMode.partial) {
        this.allSelectedMode = AllSelectedMode.partial;
    }
    if (this.selectedIds.has(assessment.assessmentId)) {
      this.selectedIds.delete(assessment.assessmentId);
    } else {
      this.selectedIds.add(assessment.assessmentId);
    }

    if (this.selectAll) {
      if (this.selectedIds.size === 0) {
        this.selectAll.nativeElement.indeterminate = false;
      } else {
        this.selectAll.nativeElement.indeterminate = true;
      }
    }
  }

  toggleSelectAll(e) {
    switch (this.allSelectedMode) {
      case AllSelectedMode.all:
        this.allSelectedMode = AllSelectedMode.none;
        this.selectedIds.clear();
        break;
      case AllSelectedMode.none:
      case AllSelectedMode.partial:
        this.allSelectedMode = AllSelectedMode.all;
        this.selectedIds = new Set(this.filteredAssessmentSearchResponseIds || this.allAssessmentSearchResponseIds);
        break;
    }
  }

  onSpeciesSelectorEvent($event: any) {
    if ($event === 'Closed') {
      this.loadAssessments();
    }
  }
}